// apps/client/src/pages/leads/edm/EdmCampaignPage.tsx
// /leads/edm/new and /leads/edm/:campaignId: compose, choose the audience,
// preview (server-rendered), test-send, freeze, schedule and follow a
// campaign. /leads/edm/new opens the full editor on local state and makes no
// server call until the first save (Save draft, preview, test send or freeze),
// which creates the draft and saves its content. Once the create lands, the
// route is replaced with /leads/edm/<id> whatever happens next: a content
// save the server refuses, or a chained action that fails, still lands the
// user on the real draft. The redirect carries a one-shot location state the
// fresh editor consumes and clears: { autoPreview } (preview after load),
// { actionError }, { htmlServerError } or { testNotice } (the failure
// message), and { draft } (unsaved content restored over the loaded draft).
// Backing out of an unsaved new campaign leaves nothing behind. Content and list are editable only while
// status === 'draft'; there is no unfreeze. Every rule shown here is
// re-checked by the server.
//
// Plan-v3 core (Slice E1): every new control renders only when its strict
// flag helper is true: edm_content (template gallery, Blocks, HTML authoring
// guide, tags applied to leads and clients, paged count), edm_tracking (A/B
// subject test, approximate opens and clicks, resend to non-openers) and
// edm_preferences (topic). With all three absent the page, and every request
// it sends, is the Phase 1c page (EdmCampaignPage.flagoff.test.tsx).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, MailPlus } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { journeyErrorMessage, useEdmJourneys, type EdmJourneyDetail } from '../../../hooks/useEdmJourneys';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { useAuth } from '../../../hooks/useAuth';
import { useLeadCaptureOnboarding } from '../../../hooks/useLeadCaptureOnboarding';
import { useEdmCampaigns } from '../../../hooks/useEdmCampaigns';
import type {
  EdmAbTest,
  EdmAudience,
  EdmCampaignProjection,
  EdmContentPath,
  EdmFixedLayout,
  EdmPreview,
  EdmUpdateFields,
} from '../../../hooks/useEdmCampaigns';
import { normaliseEdmBlocks, useEdmContent } from '../../../hooks/useEdmContent';
import type { EdmBlock, EdmSaveTemplateInput, EdmTemplate } from '../../../hooks/useEdmContent';
import {
  EDM_SUBJECT_MERGE_FIELDS,
  EDM_UI,
  canImportEdmContacts,
  edmContentRejectionIsAboutHtml,
  edmErrorMessage,
  isEdmContentEnabled,
  isEdmPreferencesEnabled,
  isEdmTrackingEnabled,
  mergeLintError,
  statusLabel,
} from '../../../utils/edm';
import ContentPathPicker from './components/ContentPathPicker';
import BlockEditor, { blocksLint } from './components/BlockEditor';
import TemplateGallery from './components/TemplateGallery';
import HtmlAuthoringGuide from './components/HtmlAuthoringGuide';
import AbTestPanel, { abTestLint } from './components/AbTestPanel';
import TopicSelect from './components/TopicSelect';
import CampaignStatsPanel from './components/CampaignStatsPanel';
import FixedLayoutFields, { fixedLayoutComplete, fixedLayoutLint } from './components/FixedLayoutFields';
import CustomHtmlPanel, { customHtmlLint } from './components/CustomHtmlPanel';
import MergeFieldChips from './components/MergeFieldChips';
import SenderIdentityPanel, { senderIdentityComplete } from './components/SenderIdentityPanel';
import type { EdmCompanyIdentity } from './components/SenderIdentityPanel';
import AudiencePanel from './components/AudiencePanel';
import { useBrokerLabels } from './components/BrokerPicker';
import PreviewPane from './components/PreviewPane';
import SchedulePanel from './components/SchedulePanel';
import StatusBanner from './components/StatusBanner';
import StatsPanel from './components/StatsPanel';

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  userVisibleOffices?: Array<{ id?: string; name?: string }>;
  companyData?: (EdmCompanyIdentity & { featureFlags?: Record<string, unknown> | null }) | null;
}

/** One-shot location state a new campaign's redirect hands the fresh editor. */
interface RedirectState {
  autoPreview?: true;
  actionError?: string;
  htmlServerError?: string;
  testNotice?: string;
  /** Content a refused save did not persist, restored after the load. */
  draft?: Draft;
}

const EMPTY_FIXED: EdmFixedLayout = { heading: '', intro: '', body: '', ctaLabel: '', ctaUrl: '', signOff: '' };
const EMPTY_AUDIENCE: EdmAudience = { includeLeads: false, includeClients: false, includeContacts: false };
const STATS_STATUSES = new Set(['sending', 'paused', 'completed', 'cancelled', 'ab_waiting']);
const CANCELLABLE = new Set(['frozen', 'scheduled', 'sending', 'paused', 'ab_waiting']);
const PAUSABLE = new Set(['scheduled', 'sending', 'ab_waiting']);
export const NEW_CAMPAIGN_NAME = 'Untitled campaign';

interface Draft {
  name: string;
  subject: string;
  contentPath: EdmContentPath;
  fixed: EdmFixedLayout;
  html: string;
  audience: EdmAudience;
  /** Send as this broker (Auth UID); null = the company. */
  sendAsBrokerUid?: string | null;
  // Plan-v3 core editor state, read and sent only when the matching flag is on.
  /** edm_content: the Blocks editor is the content (saved as custom_html + blocks). */
  useBlocks?: boolean;
  blocks?: EdmBlock[];
  /** edm_tracking: A/B subject test; null = off. */
  abTest?: EdmAbTest | null;
  /** edm_preferences. */
  topicId?: string | null;
}

/** Which plan-v3 flags are on for the company (strict helpers in utils/edm). */
interface EdmFlags {
  content: boolean;
  tracking: boolean;
  preferences: boolean;
}

function draftFrom(c: EdmCampaignProjection, flags: EdmFlags): Draft {
  const d: Draft = {
    name: c.name ?? '',
    subject: c.subject ?? '',
    contentPath: c.contentPath ?? 'fixed_layout',
    fixed: { ...EMPTY_FIXED, ...(c.fixedLayout ?? {}) },
    html: c.customHtml ?? '',
    audience: { ...EMPTY_AUDIENCE, ...(c.audience ?? {}) },
    sendAsBrokerUid: c.sendAsBrokerUid ?? null,
  };
  if (flags.content) {
    const blocks = normaliseEdmBlocks(c.blocks);
    d.blocks = blocks;
    d.useBlocks = c.contentPath === 'custom_html' && blocks.length > 0;
  }
  if (flags.tracking) d.abTest = c.abTest ?? null;
  if (flags.preferences) d.topicId = c.topicId ?? null;
  return d;
}


/** The `fixedLayout` payload: optional CTA fields only when set. */
function fixedPayload(f: EdmFixedLayout): EdmFixedLayout {
  const out: EdmFixedLayout = { heading: f.heading, intro: f.intro, body: f.body, signOff: f.signOff };
  if ((f.ctaLabel ?? '').trim()) out.ctaLabel = f.ctaLabel;
  if ((f.ctaUrl ?? '').trim()) out.ctaUrl = f.ctaUrl;
  return out;
}

const EdmCampaignEditor: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const ctx = useOutletContext<OutletCtx>();
  const { claims, user } = useAuth();
  const companyId = ctx?.userData?.companyId;
  const role = ctx?.userData?.role ?? claims?.role;
  const api = useEdmCampaigns(companyId);
  const { data: onboarding, loading: brandingLoading } = useLeadCaptureOnboarding(companyId);
  const branding = onboarding?.branding ?? null;
  const { brokers, loading: brokersLoading } = useBrokerLabels(companyId);
  const featureFlags = ctx?.companyData?.featureFlags;
  const contentOn = isEdmContentEnabled(featureFlags);
  const trackingOn = isEdmTrackingEnabled(featureFlags);
  const preferencesOn = isEdmPreferencesEnabled(featureFlags);
  const flags = useMemo<EdmFlags>(
    () => ({ content: contentOn, tracking: trackingOn, preferences: preferencesOn }),
    [contentOn, trackingOn, preferencesOn],
  );
  const contentApi = useEdmContent(companyId);
  // load() reads the flags through a ref: companyData is a live snapshot, and
  // a flag flip must never reload the draft over unsaved edits.
  const flagsRef = useRef(flags);
  flagsRef.current = flags;

  const [campaign, setCampaign] = useState<EdmCampaignProjection | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(campaignId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({
    name: campaignId ? '' : NEW_CAMPAIGN_NAME,
    subject: '',
    contentPath: 'fixed_layout',
    fixed: EMPTY_FIXED,
    html: '',
    audience: EMPTY_AUDIENCE,
    sendAsBrokerUid: null,
  });
  const location = useLocation();
  // One-shot state from a new campaign's redirect, read once on mount (and
  // cleared from history below, so a reload or back-forward never replays it).
  const arrivalRef = useRef<RedirectState | null>((location.state as RedirectState | null) ?? null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(() => arrivalRef.current?.actionError ?? null);
  const [htmlServerError, setHtmlServerError] = useState<string | null>(() => arrivalRef.current?.htmlServerError ?? null);
  const [preview, setPreview] = useState<EdmPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [testNotice, setTestNotice] = useState<string | null>(() => arrivalRef.current?.testNotice ?? null);
  const [dialog, setDialog] = useState<'freeze' | 'resume' | 'cancel' | null>(null);
  const scheduleRef = useRef<HTMLDivElement | null>(null);
  const isNew = !campaignId;
  // Set by a new campaign's preview redirect: preview once, after the load.
  const autoPreviewRef = useRef(arrivalRef.current?.autoPreview === true);
  const arrivalClearedRef = useRef(false);
  useEffect(() => {
    if (arrivalClearedRef.current || !campaignId || arrivalRef.current === null) return;
    arrivalClearedRef.current = true;
    navigate(`/leads/edm/${campaignId}`, { replace: true, state: null });
  }, [campaignId, navigate]);
  // A chained action (preview, test send, freeze) on a new campaign ends in a
  // redirect and a reload from the server; inputs stay locked until it settles.
  const [chaining, setChaining] = useState(false);
  // A save in flight (create + update): a second click must never create twice.
  const savingRef = useRef(false);
  // The id a new campaign was created under. If create succeeded but the
  // content update failed, the next save updates this id instead of creating
  // a second draft.
  const createdIdRef = useRef<string | null>(null);
  // Async paths that navigate or set state check this after every await.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Only the newest load may apply its result (a route change and a
  // lifecycle reload can overlap).
  const loadSeqRef = useRef(0);
  // The sendAsBrokerUid the server holds (last load or last successful save).
  // update sends it only when the draft differs: the uid for a broker, '' to
  // clear back to the company; omitted (unchanged) otherwise.
  const savedSendAsRef = useRef<string | null>(null);
  // edm_content: the server holds a block list, so leaving Blocks must clear it.
  const savedHadBlocksRef = useRef(false);
  // edm_content: the block list the stored HTML was compiled from (JSON).
  const loadedBlocksRef = useRef('[]');
  // Plan JOURNEYS J8: a journey step template ('journey_step') is edited here;
  // its audience, sender and topic belong to the journey. A journey child is read-only.
  const journeyApi = useEdmJourneys(companyId);
  const [journey, setJourney] = useState<EdmJourneyDetail | null>(null);

  const load = useCallback(
    async (id: string | undefined = campaignId) => {
      if (!id) return;
      const seq = ++loadSeqRef.current;
      setLoading(true);
      setLoadError(null);
      try {
        const c = await api.getCampaign(id);
        if (!mountedRef.current || seq !== loadSeqRef.current) return;
        setCampaign(c);
        savedSendAsRef.current = c.sendAsBrokerUid ?? null;
        savedHadBlocksRef.current = normaliseEdmBlocks(c.blocks).length > 0;
        loadedBlocksRef.current = JSON.stringify(normaliseEdmBlocks(c.blocks));
        setDraft(draftFrom(c, flagsRef.current));
        setDirty(false);
      } catch (err) {
        if (!mountedRef.current || seq !== loadSeqRef.current) return;
        setLoadError(edmErrorMessage(err));
      } finally {
        if (mountedRef.current && seq === loadSeqRef.current) setLoading(false);
      }
    },
    [api, campaignId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const status = campaign?.status ?? 'draft';
  const isStep = (status as string) === 'journey_step';
  const journeyId = campaign?.journeyId ?? null;
  useEffect(() => {
    if (!isStep || !journeyId) return;
    let live = true;
    journeyApi
      .get(journeyId)
      .then((j) => {
        if (live) setJourney(j);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [isStep, journeyId, journeyApi]);
  // A new campaign is editable at once. An existing one is editable only once
  // it has loaded as a draft. On a load failure the form stays read-only behind
  // the error banner, so a Save can never overwrite a campaign whose real
  // state is unknown.
  const loaded = isNew || campaign !== null;
  const editable = isNew || (campaign !== null && (status === 'draft' || (isStep && journey?.status !== 'active')));
  const canImport = canImportEdmContacts(role, claims?.canSeeAllOffices);
  const company = ctx?.companyData ?? null;
  // The audience as the server will see it: contacts only for import roles.
  const effectiveAudience = useMemo<EdmAudience>(
    () => (canImport ? draft.audience : { ...draft.audience, includeContacts: false }),
    [canImport, draft.audience],
  );
  const senderComplete = senderIdentityComplete(branding, company);

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  // ----- client lint (not authority; the server re-checks every rule)
  const subjectLint = mergeLintError(draft.subject);
  const blocksMode = contentOn && draft.useBlocks === true;
  const contentLintOk = useMemo(
    () =>
      blocksMode
        ? blocksLint(draft.blocks ?? []) === null
        : draft.contentPath === 'fixed_layout'
          ? Object.keys(fixedLayoutLint(draft.fixed)).length === 0
          : customHtmlLint(draft.html) === null,
    [blocksMode, draft.blocks, draft.contentPath, draft.fixed, draft.html],
  );
  const abLintOk = !trackingOn || abTestLint(draft.abTest ?? null, draft.subject) === null;
  const lintOk = subjectLint === null && contentLintOk && abLintOk;
  const contentComplete =
    draft.subject.trim().length > 0 &&
    (blocksMode
      ? (draft.blocks ?? []).length > 0
      : draft.contentPath === 'fixed_layout'
        ? fixedLayoutComplete(draft.fixed)
        : draft.html.trim().length > 0);
  // edm_content: body content in the mode shown (what Save as template stores),
  // and anything visible a template would replace.
  const bodyContent = blocksMode
    ? (draft.blocks ?? []).length > 0
    : draft.contentPath === 'fixed_layout'
      ? Object.values(draft.fixed).some((v) => (v ?? '').trim().length > 0)
      : draft.html.trim().length > 0;
  const anyContent = bodyContent || draft.subject.trim().length > 0;
  const audienceChosen = draft.audience.includeLeads || draft.audience.includeClients || draft.audience.includeContacts;

  // ----- edm_content template gallery
  const templateContent = useCallback(
    (): Omit<EdmSaveTemplateInput, 'name' | 'templateId'> =>
      blocksMode
        ? { subject: draft.subject, contentPath: 'custom_html', fixedLayout: null, customHtml: null, blocks: draft.blocks ?? [] }
        : {
            subject: draft.subject,
            contentPath: draft.contentPath,
            fixedLayout: draft.contentPath === 'fixed_layout' ? fixedPayload(draft.fixed) : null,
            customHtml: draft.contentPath === 'custom_html' ? draft.html : null,
          },
    [blocksMode, draft.blocks, draft.contentPath, draft.fixed, draft.html, draft.subject],
  );
  const applyTemplate = (t: EdmTemplate) => {
    const blocks = normaliseEdmBlocks(t.blocks);
    const useBlocks = blocks.length > 0;
    update({
      subject: t.subject ?? '',
      contentPath: useBlocks ? 'custom_html' : t.contentPath,
      useBlocks,
      blocks,
      fixed: { ...EMPTY_FIXED, ...(t.fixedLayout ?? {}) },
      html: useBlocks ? '' : t.customHtml ?? '',
    });
  };

  const nameOk = draft.name.trim().length > 0;
  // A new campaign's first save, and any chained action that ends in a redirect
  // and a reload from the server, would discard anything typed meanwhile, so
  // its inputs are locked until they settle.
  const fieldsLocked = !editable || (isNew && (busy || chaining));
  const canSave = editable && nameOk && lintOk && !busy && !chaining;
  const contentValid = nameOk && lintOk && contentComplete;
  const canFreeze = status === 'draft' && contentValid && senderComplete && audienceChosen && !busy && !chaining;
  const canTest = (status === 'draft' || status === 'frozen' || isStep) && contentValid && senderComplete;

  const scopeLine = useMemo(() => {
    if (role === 'administrator' && claims?.canSeeAllOffices !== true) {
      const mine = new Set(claims?.officeIds ?? []);
      const names = (ctx?.userVisibleOffices ?? [])
        .filter((o) => o.id && mine.has(o.id))
        .map((o) => o.name ?? o.id)
        .join(', ');
      return `Limited to your offices: ${names || 'your assigned offices'}.`;
    }
    return 'Company-wide.';
  }, [role, claims?.canSeeAllOffices, claims?.officeIds, ctx?.userVisibleOffices]);

  /**
   * Persist the draft and return its campaign id, or null when the server
   * refused. A new campaign is created on its first save and its content saved
   * against the new id; with `redirect` the route is then replaced with
   * /leads/edm/<id> (a fresh mount). Chained actions pass redirect: false and
   * redirect themselves once they finish, so the remount cannot cut them off.
   */
  const save = useCallback(async ({ redirect = true }: { redirect?: boolean } = {}): Promise<string | null> => {
    if (savingRef.current) return null;
    savingRef.current = true;
    setActionError(null);
    setHtmlServerError(null);
    setBusy(true);
    try {
      let id = campaignId ?? createdIdRef.current;
      if (!id) {
        id = (await api.create(draft.name.trim())).campaignId;
        createdIdRef.current = id;
      }
      const sendAs = draft.sendAsBrokerUid ?? null;
      const sendAsChanged = sendAs !== savedSendAsRef.current;
      const fields: EdmUpdateFields = {
        campaignId: id,
        name: draft.name.trim(),
        subject: draft.subject,
        contentPath: blocksMode ? 'custom_html' : draft.contentPath,
        // edm_content Blocks: the server compiles the list to the stored HTML.
        ...(blocksMode
          ? { blocks: draft.blocks ?? [] }
          : draft.contentPath === 'fixed_layout'
            ? { fixedLayout: fixedPayload(draft.fixed) }
            : { customHtml: draft.html }),
        audience: effectiveAudience,
        // Left Blocks: clear the stored list so it cannot overwrite this content.
        ...(contentOn && !blocksMode && savedHadBlocksRef.current ? { blocks: null } : {}),
      };
      // A journey step: the sender, topic and audience are the journey's (the
      // server ignores them) and journeys never A/B test.
      if (sendAsChanged && !isStep) fields.sendAsBrokerUid = sendAs ?? '';
      // Sent only when the draft was loaded with the flag on (undefined = never
      // read), so a flag switched on mid-session never clears stored values.
      if (trackingOn && draft.abTest !== undefined && !isStep) fields.abTest = draft.abTest;
      if (preferencesOn && draft.topicId !== undefined && !isStep) fields.topicId = draft.topicId;
      await api.update(fields);
      savedSendAsRef.current = sendAs;
      if (contentOn) savedHadBlocksRef.current = blocksMode;
      if (!mountedRef.current) return id;
      setDirty(false);
      // Blocks: the server compiled new HTML; reload it so "Paste your own HTML"
      // never shows (or re-saves) the HTML of an older block list.
      if (blocksMode && campaignId) void load(campaignId);
      if (!campaignId && redirect) navigate(`/leads/edm/${id}`, { replace: true });
      return id;
    } catch (err) {
      if (!mountedRef.current) return null;
      const msg = isStep ? journeyErrorMessage(err) : edmErrorMessage(err);
      // Beside the HTML or block editor only when the refusal is about that
      // content and the editor is on screen; a subject, fixed-layout or
      // missing-content refusal is a page-level error (the editor banner is
      // not rendered for the fixed layout, so it would never be seen).
      const rejected = edmContentRejectionIsAboutHtml(err) && (blocksMode || draft.contentPath === 'custom_html');
      const createdId = campaignId ? null : createdIdRef.current;
      if (createdId) {
        // Created, but the content save failed: the draft is real, so land on
        // it with the error and the unsaved content rather than leave its id
        // only in this editor, where a reload would orphan it.
        navigate(`/leads/edm/${createdId}`, {
          replace: true,
          state: { ...(rejected ? { htmlServerError: msg } : { actionError: msg }), draft },
        });
        return null;
      }
      if (rejected) setHtmlServerError(msg);
      else setActionError(msg);
      return null;
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setBusy(false);
    }
  }, [api, blocksMode, campaignId, contentOn, draft, effectiveAudience, isStep, load, navigate, preferencesOn, trackingOn]);

  /**
   * The campaign id with unsaved edits persisted first (never redirecting), or
   * null. A save that Save draft would refuse (blank name, lint) is refused
   * here too, with the reason passed to `refuse` for the caller's own pane.
   */
  const savedId = useCallback(
    async (refuse: (reason: string) => void): Promise<string | null> => {
      // A new campaign needs its first save; after that (created, clean) it
      // needs one only when edited, like an existing campaign.
      const needsSave = (dirty && editable) || (isNew && createdIdRef.current === null);
      if (!needsSave) return campaignId ?? createdIdRef.current;
      if (!nameOk) {
        refuse('Give the campaign a name first.');
        return null;
      }
      if (!lintOk) {
        refuse('Fix the highlighted fields first.');
        return null;
      }
      return save({ redirect: false });
    },
    [campaignId, dirty, editable, isNew, lintOk, nameOk, save],
  );

  /** After a chained action on a new campaign, open its own route. */
  const openCreated = useCallback(
    (id: string, state?: RedirectState) => {
      if (isNew && mountedRef.current) navigate(`/leads/edm/${id}`, { replace: true, state });
    },
    [isNew, navigate],
  );

  /** Run a chained action; on a new campaign the inputs stay locked throughout. */
  const chained = async (fn: () => Promise<void>) => {
    if (isNew) setChaining(true);
    try {
      await fn();
    } finally {
      if (mountedRef.current) setChaining(false);
    }
  };

  const refreshPreview = useCallback(async () => {
    setPreviewError(null);
    const id = await savedId(setPreviewError);
    if (!id || !mountedRef.current) return;
    if (isNew) {
      // Persisted now: open its own route and preview there, so the URL, the
      // pane and a reload all see a saved campaign.
      openCreated(id, { autoPreview: true });
      return;
    }
    setPreviewLoading(true);
    try {
      const p = await api.preview(id);
      if (mountedRef.current) setPreview(p);
    } catch (err) {
      if (mountedRef.current) setPreviewError(edmErrorMessage(err));
    } finally {
      if (mountedRef.current) setPreviewLoading(false);
    }
  }, [api, isNew, openCreated, savedId]);

  useEffect(() => {
    if (!autoPreviewRef.current || campaign === null) return;
    autoPreviewRef.current = false;
    void refreshPreview();
  }, [campaign, refreshPreview]);

  // Unsaved content carried across a refused-save redirect: restore it over
  // the loaded draft, marked dirty, so nothing the user typed is lost.
  const arrivalDraftRef = useRef<Draft | null>(arrivalRef.current?.draft ?? null);
  useEffect(() => {
    const carried = arrivalDraftRef.current;
    if (carried === null || campaign === null) return;
    arrivalDraftRef.current = null;
    if (campaign.status !== 'draft' && (campaign.status as string) !== 'journey_step') return;
    setDraft(carried);
    setDirty(true);
  }, [campaign]);

  const sendTest = async () => {
    setTestNotice(null);
    const id = await savedId(setTestNotice);
    if (!id || !mountedRef.current) return;
    setSendingTest(true);
    try {
      await api.sendTest(id);
      toast.success(`Test sent to ${user?.email ?? 'your email'}`);
      openCreated(id);
    } catch (err) {
      const message = edmErrorMessage(err);
      // On /new the draft exists now: land on it, carrying the message.
      if (isNew) openCreated(id, { testNotice: message });
      else if (mountedRef.current) setTestNotice(message);
    } finally {
      if (mountedRef.current) setSendingTest(false);
    }
  };

  /**
   * Run a lifecycle action on campaign `id`, then reload its projection (unless
   * `skipReload`: a redirect follows and the fresh mount loads it). Returns
   * null on success, else the error message (also shown in the banner).
   */
  const run = async (
    id: string,
    fn: () => Promise<unknown>,
    success?: string,
    { skipReload = false }: { skipReload?: boolean } = {},
  ): Promise<string | null> => {
    setActionError(null);
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
      if (!skipReload) await load(id);
      return null;
    } catch (err) {
      const message = edmErrorMessage(err);
      if (mountedRef.current) setActionError(message);
      return message;
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const freeze = async () => {
    setActionError(null);
    const id = await savedId(setActionError);
    if (!id) return;
    const error = await run(id, () => api.freeze(id), undefined, { skipReload: isNew });
    // On /new the draft exists whatever the outcome: land on it.
    openCreated(id, error ? { actionError: error } : undefined);
  };

  // An existing campaign shows the skeleton until it has loaded or failed, so
  // the empty, disabled form never flashes before the first load.
  if (!isNew && campaign === null && loadError === null) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const headerActions = loaded && (
    <div className="flex flex-wrap items-center gap-2">
      {editable && (
        <button type="button" className={EDM_UI.secondaryButton} disabled={!canSave} onClick={() => void save()}>
          {isStep ? 'Save email' : 'Save draft'}
        </button>
      )}
      {status === 'draft' && (
        <button type="button" className={EDM_UI.primaryButton} disabled={!canFreeze} onClick={() => setDialog('freeze')}>
          Freeze list
        </button>
      )}
      {status === 'frozen' && (
        <button
          type="button"
          className={EDM_UI.primaryButton}
          onClick={() => scheduleRef.current?.scrollIntoView({ behavior: 'smooth' })}
        >
          Schedule
        </button>
      )}
      {PAUSABLE.has(status) && campaignId && !journeyId && (
        <button type="button" className={EDM_UI.secondaryButton} disabled={busy} onClick={() => void run(campaignId, () => api.pause(campaignId))}>
          Pause
        </button>
      )}
      {CANCELLABLE.has(status) && !journeyId && (
        <button type="button" className={EDM_UI.dangerButton} disabled={busy} onClick={() => setDialog('cancel')}>
          Cancel campaign
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {journeyId ? (
        <Link to={`/leads/edm/journeys/${journeyId}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200">
          <ArrowLeft className="h-4 w-4" /> Back to journey
        </Link>
      ) : (
        <Link to="/leads/edm" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200">
          <ArrowLeft className="h-4 w-4" /> Back to campaigns
        </Link>
      )}
      <PageHeader
        icon={MailPlus}
        title={campaign?.name || 'New campaign'}
        subtitle={isStep ? 'Journey email' : statusLabel(status)}
        badge={status === 'paused' ? 'Paused' : undefined}
        onRefresh={isNew ? undefined : () => void load()}
        isRefreshing={loading}
        actions={headerActions}
      />

      {loadError && <div className={EDM_UI.errorBanner}>{loadError}</div>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="space-y-6 xl:col-span-3">
          <StatusBanner
            status={status}
            pausedReason={campaign?.pausedReason ?? null}
            updatedAt={campaign?.updatedAt ?? null}
            role={role}
            actionError={actionError}
            busy={busy}
            onResume={() => {
              // A journey child is resumed only through its journey.
              if (!journeyId) setDialog('resume');
            }}
          />

          {campaign !== null && !editable && !journeyId && (
            <div className={EDM_UI.infoBanner}>
              This campaign&apos;s content and list are frozen. Cancel and create a new campaign to change them.
            </div>
          )}
          {journeyId && !isStep && (
            <div className={EDM_UI.infoBanner}>
              This email is sent by a journey{campaign?.journeyDay ? ` on ${campaign.journeyDay}` : ''}. Manage it from the journey.
            </div>
          )}
          {isStep && journey?.status === 'active' && (
            <div className={EDM_UI.infoBanner}>This journey is active. Pause it to edit its emails.</div>
          )}
          {isStep && (
            <div className={EDM_UI.infoBanner}>
              Send a test after every change and wait for it to be delivered: the journey cannot start or resume until each email&apos;s latest test is delivered.
            </div>
          )}
          {isStep && journey?.trigger.type === 'fixed_rate_expiry' && (
            <div className={EDM_UI.warnBanner}>
              Fixed-rate end dates come from your CRM and can be out of date. Keep the wording soft (&quot;your fixed rate may be ending soon&quot;) and offer a
              review; never state a firm date.{contentOn ? ' The Fixed-rate review template is a good start.' : ''}
            </div>
          )}

          <div className={EDM_UI.card}>
            <h2 className={EDM_UI.heading}>Details</h2>
            <div>
              <label htmlFor="edm-name" className={EDM_UI.label}>
                Name
              </label>
              <input
                id="edm-name"
                className={EDM_UI.input}
                maxLength={120}
                value={draft.name}
                disabled={fieldsLocked}
                onChange={(e) => update({ name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="edm-subject" className={EDM_UI.label}>
                Subject line
              </label>
              <input
                id="edm-subject"
                className={EDM_UI.input}
                maxLength={200}
                value={draft.subject}
                disabled={fieldsLocked}
                onChange={(e) => update({ subject: e.target.value })}
              />
              <div className="flex items-center justify-between">
                <MergeFieldChips
                  fields={EDM_SUBJECT_MERGE_FIELDS}
                  disabled={fieldsLocked}
                  onInsert={(t) => update({ subject: (draft.subject + t).slice(0, 200) })}
                />
                <span className="text-xs text-gray-500">{draft.subject.length}/200</span>
              </div>
              {subjectLint && <p className="text-xs text-red-400">{subjectLint}</p>}
            </div>
            {trackingOn && !isStep && (
              <AbTestPanel
                value={draft.abTest ?? null}
                subjectA={draft.subject}
                disabled={fieldsLocked}
                onChange={(abTest) => update({ abTest })}
              />
            )}
            {preferencesOn && !isStep && (
              <TopicSelect
                value={draft.topicId ?? null}
                disabled={fieldsLocked}
                getTopics={contentApi.getTopics}
                onChange={(topicId) => update({ topicId })}
              />
            )}
          </div>

          <div className={EDM_UI.card}>
            <h2 className={EDM_UI.heading}>Content</h2>
            {contentOn && (
              <TemplateGallery
                listTemplates={contentApi.listTemplates}
                saveTemplate={contentApi.saveTemplate}
                deleteTemplate={contentApi.deleteTemplate}
                currentContent={templateContent}
                hasContent={anyContent}
                canSaveTemplate={bodyContent}
                onApply={applyTemplate}
                disabled={fieldsLocked}
              />
            )}
            <ContentPathPicker
              value={blocksMode ? 'blocks' : draft.contentPath}
              disabled={fieldsLocked}
              {...(contentOn ? { allowBlocks: true } : {})}
              onChange={(mode) =>
                update(mode === 'blocks' ? { useBlocks: true, contentPath: 'custom_html' } : { useBlocks: false, contentPath: mode })
              }
            />
            {blocksMode ? (
              <>
                <BlockEditor blocks={draft.blocks ?? []} disabled={fieldsLocked} onChange={(blocks) => update({ blocks })} />
                {htmlServerError && <div className={EDM_UI.errorBanner}>{htmlServerError}</div>}
              </>
            ) : draft.contentPath === 'fixed_layout' ? (
              <FixedLayoutFields value={draft.fixed} disabled={fieldsLocked} onChange={(fixed) => update({ fixed })} />
            ) : (
              <>
                {contentOn && <HtmlAuthoringGuide />}
                {contentOn && savedHadBlocksRef.current && JSON.stringify(draft.blocks ?? []) !== loadedBlocksRef.current && (
                  <div className={EDM_UI.warnBanner}>
                    This HTML was made from your blocks at the last save, so block changes since then are not in it. Switch
                    back to Blocks and save first to include them.
                  </div>
                )}
                <CustomHtmlPanel
                  value={draft.html}
                  disabled={fieldsLocked}
                  serverError={htmlServerError}
                  onChange={(html) => update({ html })}
                />
              </>
            )}
          </div>

          {!journeyId && (
          <SenderIdentityPanel
            branding={branding}
            company={company}
            fromDomain={campaign?.fromDomain ?? null}
            sendAsBrokerUid={draft.sendAsBrokerUid ?? null}
            onSendAsChange={(sendAsBrokerUid) => update({ sendAsBrokerUid })}
            brokers={brokers}
            loadCandidates={api.listSenderCandidates}
            loadOnMount={!isNew && editable}
            disabled={fieldsLocked}
            loading={brandingLoading && !onboarding}
          />
          )}

          {journeyId ? (
            <div className={EDM_UI.card}>
              <h2 className={EDM_UI.heading}>Audience and sender</h2>
              <p className="text-sm text-gray-300">Set on the journey: who it emails, who it is sent as and its topic.</p>
            </div>
          ) : (
          <AudiencePanel
            audience={draft.audience}
            onChange={(audience) => update({ audience })}
            disabled={fieldsLocked}
            role={role}
            canImport={canImport}
            scopeLine={scopeLine}
            counters={campaign?.frozenAt ? campaign.counters : null}
            brokers={brokers}
            brokersLoading={brokersLoading}
            effectiveAudience={effectiveAudience}
            countAudience={api.countAudience}
            listTags={api.listTags}
            listSegments={api.listSegments}
            saveSegment={api.saveSegment}
            deleteSegment={api.deleteSegment}
            {...(contentOn ? { contentEnabled: true, countAudiencePage: api.countAudiencePage } : {})}
          />
          )}

          {!isStep && (
          <div ref={scheduleRef}>
            <SchedulePanel
              status={status}
              scheduledAt={campaign?.scheduledAt ?? null}
              recipients={campaign?.counters?.recipients ?? 0}
              senderComplete={senderComplete}
              busy={busy}
              onSchedule={(iso) =>
                campaignId ? run(campaignId, () => api.schedule(campaignId, iso), 'Campaign scheduled').then(() => undefined) : Promise.resolve()
              }
            />
          </div>
          )}

          {campaignId && STATS_STATUSES.has(status) &&
            (trackingOn ? (
              <CampaignStatsPanel
                campaignId={campaignId}
                status={status}
                completedAt={campaign?.completedAt ?? null}
                getStats={api.getStats}
                createResend={journeyId ? undefined : api.createResend}
                onResendCreated={(childId) => navigate(`/leads/edm/${childId}`)}
              />
            ) : (
              <StatsPanel campaignId={campaignId} status={status} getStats={api.getStats} />
            ))}
        </div>

        <div className="xl:col-span-2">
          <PreviewPane
            preview={preview}
            loading={previewLoading}
            error={previewError}
            dirty={(dirty || isNew) && editable}
            canRefresh={lintOk && !busy && !chaining}
            onRefresh={() => void chained(refreshPreview)}
            testEmail={user?.email ?? null}
            canSendTest={canTest && !busy && !chaining}
            sendingTest={sendingTest}
            testNotice={testNotice}
            onSendTest={() => void chained(sendTest)}
          />
        </div>
      </div>

      <ConfirmDialog
        isOpen={dialog === 'freeze'}
        title="Freeze the recipient list?"
        message="We take a snapshot of who will receive this email. People who join later are not added. You can still cancel."
        confirmLabel="Freeze list"
        confirmVariant="primary"
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          setDialog(null);
          void chained(freeze);
        }}
      />
      <ConfirmDialog
        isOpen={dialog === 'resume'}
        title="Resume sending?"
        message="Recipients who unsubscribed or became ineligible while paused will be skipped."
        confirmLabel="Resume"
        confirmVariant="primary"
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          setDialog(null);
          if (campaignId) void run(campaignId, () => api.resume(campaignId));
        }}
      />
      <ConfirmDialog
        isOpen={dialog === 'cancel'}
        title="Cancel this campaign?"
        message="No more emails will be sent. Emails already sent cannot be recalled. This cannot be undone."
        confirmLabel="Cancel campaign"
        cancelLabel="Keep campaign"
        confirmVariant="danger"
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          setDialog(null);
          if (campaignId) void run(campaignId, () => api.cancel(campaignId));
        }}
      />
    </div>
  );
};

/**
 * /leads/edm/new and /leads/edm/:campaignId render the same route element, so
 * React would keep one instance (campaign, draft, preview, created id) across
 * a route change: /<id> -> /new would then save into the OLD campaign. Keying
 * on the route identity gives every campaign, and every /new visit, a fresh
 * editor; the post-create redirect is a fresh mount that loads the new id.
 */
const EdmCampaignPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  return <EdmCampaignEditor key={campaignId ?? 'new'} />;
};

export default EdmCampaignPage;
